import { storeToRefs } from "pinia";
import { computed } from "vue";
import { type RouteLocationNormalizedGeneric } from "vue-router";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import features from "shared/services/features";
import { useFilterSetsStore } from "shared/stores/filterSets";
import { useGlobalStore } from "shared/stores/global";
import { useOutreachContactsStore } from "shared/stores/outreachContacts";
import { useStreamsStore } from "shared/stores/streams";
import { useUserStore } from "shared/stores/user";
import { type OrganisationTeam } from "shared/stores/user/types";
import { type Maybe, type Nullable } from "shared/types";

import useProductRouting from "./useProductRouting";

export default function useTeamPicker(route?: RouteLocationNormalizedGeneric) {
  const userStore = useUserStore();

  const {
    currentUser,
    preferences,
    sortedAllOrganisationTeams,
    sortedUserOrganisationTeams,
  } = storeToRefs(userStore);

  const { isSuperRole, updateUserPreferences } = userStore;

  const outreachContactsStore = useOutreachContactsStore();

  const streamsStore = useStreamsStore();
  const { getStreams } = streamsStore;

  const filterSetsStore = useFilterSetsStore();
  const { getFilterSets } = filterSetsStore;

  const globalStore = useGlobalStore();
  const { routerViewKey } = storeToRefs(globalStore);
  const { setRouterViewKey } = globalStore;

  const { routeProductName } = useProductRouting();

  const currentRouteProductMenu = computed(() => routeProductName(route));

  const selectedTeamPreference = computed<number | undefined>(
    () =>
      preferences.value[`selected_team_${currentRouteProductMenu.value}`] as
        | number
        | undefined
  );

  const isUserRoleSuper = computed(() =>
    isSuperRole(currentRouteProductMenu.value)
  );

  const organisationTeamOptions = computed(() => {
    if (features.has("has_team_workspaces")) {
      return isUserRoleSuper.value
        ? sortedAllOrganisationTeams.value
        : sortedUserOrganisationTeams.value;
    }

    return [];
  });

  const showTeamPicker = computed(() =>
    Boolean(organisationTeamOptions.value.length > 1)
  );

  const isTeamOptionsAvailable = computed(() =>
    Boolean(organisationTeamOptions.value.length)
  );

  const defaultTeamId = computed(() =>
    isTeamOptionsAvailable.value
      ? currentUser.value.primary_organisation_team_id ||
        organisationTeamOptions.value[0]?.id
      : null
  );

  const selectedTeamId = computed(
    () => selectedTeamPreference.value ?? defaultTeamId.value ?? undefined
  );

  async function updateSelectedTeamPreference(teamId: Nullable<number>) {
    const isOutreachWorkspaceChange =
      currentRouteProductMenu.value === "outreach" &&
      selectedTeamId.value !== teamId;

    try {
      await updateUserPreferences({
        attribute: `selected_team_${currentRouteProductMenu.value}`,
        id: currentUser.value.id,
        value: teamId,
      });

      getStreams();
      getFilterSets();

      if (isOutreachWorkspaceChange) {
        outreachContactsStore.startNewSearch();
      }

      if (teamId && routerViewKey.value !== "") {
        setRouterViewKey(teamId);
      }
    } catch (error) {
      ErrorDialog(
        getLocaleText("team_picker.errors.updating_team_preference"),
        error
      );
    }
  }

  function updateTeamPreferenceMismatch(organisationTeamId: number) {
    if (
      isTeamOptionsAvailable.value &&
      selectedTeamPreference.value !== organisationTeamId
    ) {
      updateSelectedTeamPreference(organisationTeamId);
    }
  }

  function organisationTeamFromId(id: number): Maybe<OrganisationTeam> {
    return organisationTeamOptions.value.find(
      (organisationTeam) => organisationTeam.id === id
    );
  }

  const organisationTeamFromPreference = computed<Maybe<OrganisationTeam>>(
    () =>
      selectedTeamPreference.value
        ? organisationTeamFromId(selectedTeamPreference.value)
        : undefined
  );

  function teamSelectorFilterParams(teamId?: number): string | string[] {
    if (!teamId) {
      return organisationTeamOptions.value
        .map((team) => String(team.id))
        .concat(["null"]);
    }

    return String(teamId);
  }

  return {
    defaultTeamId,
    isUserRoleSuper,
    organisationTeamFromId,
    organisationTeamFromPreference,
    organisationTeamOptions,
    selectedTeamId,
    selectedTeamPreference,

    showTeamPicker,
    teamSelectorFilterParams,
    updateSelectedTeamPreference,
    updateTeamPreferenceMismatch,
  };
}
